import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { blogImages } from "@/lib/blogImages";

export default function Blog() {
    const { t } = useLanguage();
    const b = t.blogPage;
    const [featured, ...rest] = t.blog.posts;

    return (
        <div data-testid="blog-page" className="pt-36 sm:pt-44">
            <section className="relative mx-auto max-w-7xl px-5 sm:px-8">
                <div className="grid-bg absolute inset-0 -z-10" aria-hidden="true" />
                <Reveal>
                    <Eyebrow>{b.eyebrow}</Eyebrow>
                    <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
                        {b.title}
                    </h1>
                    <p className="mt-6 max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{b.sub}</p>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
                {/* FEATURED POST */}
                <Reveal>
                    <Link
                        to={`/blog/${featured.slug}`}
                        data-testid={`blog-card-${featured.slug}`}
                        className="glass card-lift group grid overflow-hidden rounded-3xl lg:grid-cols-2"
                    >
                        <div className="relative overflow-hidden">
                            <img
                                src={blogImages[featured.slug]}
                                alt={featured.title}
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <span className="absolute left-5 top-5 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-neon backdrop-blur">
                                {featured.category}
                            </span>
                        </div>
                        <div className="flex flex-col justify-center p-8 sm:p-12">
                            <p className="font-mono text-[11px] text-slate-500">
                                {featured.date} · {featured.readTime}
                            </p>
                            <h2 className="mt-4 font-display text-xl font-bold leading-snug tracking-tight text-white sm:text-2xl lg:text-3xl">
                                {featured.title}
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-slate-400">{featured.excerpt}</p>
                            <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-neon">
                                {t.home.readMore}
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </span>
                        </div>
                    </Link>
                </Reveal>

                {/* REST */}
                <div className="mt-8 grid gap-5 md:grid-cols-3">
                    {rest.map((post, i) => (
                        <Reveal key={post.slug} delay={0.08 * i}>
                            <Link
                                to={`/blog/${post.slug}`}
                                data-testid={`blog-card-${post.slug}`}
                                className="glass card-lift group flex h-full flex-col overflow-hidden rounded-2xl"
                            >
                                <div className="relative overflow-hidden">
                                    <img
                                        src={blogImages[post.slug]}
                                        alt={post.title}
                                        loading="lazy"
                                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span className="absolute left-4 top-4 rounded-full bg-ink/80 px-3 py-1 font-mono text-[10px] uppercase tracking-widest text-neon backdrop-blur">
                                        {post.category}
                                    </span>
                                </div>
                                <div className="flex flex-1 flex-col p-6">
                                    <p className="font-mono text-[10px] text-slate-500">
                                        {post.date} · {post.readTime}
                                    </p>
                                    <h3 className="mt-3 flex-1 font-display text-base font-bold leading-snug text-white sm:text-lg">
                                        {post.title}
                                    </h3>
                                    <p className="mt-3 line-clamp-3 text-xs leading-relaxed text-slate-400 sm:text-sm">
                                        {post.excerpt}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-neon">
                                        {t.home.readMore}
                                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                                    </span>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>
        </div>
    );
}
